import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyBiometricDevices,
  CompanyBiometricDevicesListPage,
  CompanyUnmappedEventsPage,
  CompanyBiometricDevicesSettingsPage,
  CompanyDeviceEventsPage,
} from "@/utils/lazyLoadComponents";

export const COMPANY_BIOMETRIC_DEVICE_DYNAMIC_ID_KEY = "id";

export enum COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS {
  ROOT = "biometric-devices",
  DEVICES = "devices",
  UNMAPPED_EVENTS = "unmapped-events",
  // Module settings (agents/pairing). Literal, not `MODULE_SETTINGS_SEGMENT`:
  // `isolatedModules` forbids enum members initialized from an imported const.
  // Keep both in sync.
  SETTINGS = "settings",
  // Single-device event log. Literal, not built from COMPANY_BIOMETRIC_DEVICE_DYNAMIC_ID_KEY:
  // same isolatedModules restriction as SETTINGS above.
  DEVICE_EVENTS = "devices/:id/events",
}

export const companyBiometricDevicesRoutes: RouteObject = {
  path: COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Biometric Devices" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own table ──────────────────
    {
      Component: CompanyBiometricDevices,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.DEVICES),
        },
        {
          path: COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.DEVICES,
          handle: { breadcrumb: "Devices" },
          Component: CompanyBiometricDevicesListPage,
        },
        {
          path: COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.UNMAPPED_EVENTS,
          handle: { breadcrumb: "Unmapped Events" },
          Component: CompanyUnmappedEventsPage,
        },
      ],
    },
    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab. ──
    {
      path: COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.SETTINGS,
      handle: {
        breadcrumb: "Settings",
        title: "Biometric Devices Settings",
      },
      Component: CompanyBiometricDevicesSettingsPage,
    },
    // ── Single-device event log — also a SIBLING of the tabs layout, same
    // reasoning as SETTINGS above (see routes.audit-logs.ts DETAILS for the
    // identical shape). ────────────────────────────────────────────────────
    {
      path: COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.DEVICE_EVENTS,
      handle: { breadcrumb: "Events", title: "Device Events" },
      Component: CompanyDeviceEventsPage,
    },
  ],
};

export const COMPANY_BIOMETRIC_DEVICES_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(
    addSlashes(COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.ROOT),
  ),
  DEVICES: withCompanyPrefix(
    addSlashes(
      COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.ROOT,
      COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.DEVICES,
    ),
  ),
  UNMAPPED_EVENTS: withCompanyPrefix(
    addSlashes(
      COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.ROOT,
      COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.UNMAPPED_EVENTS,
    ),
  ),
  DEVICE_EVENTS: (id: string) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.ROOT,
        COMPANY_BIOMETRIC_DEVICES_PATH_SEGMENTS.DEVICES,
        id,
        "events",
      ),
    ),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.BIOMETRIC_DEVICES.ROOT,
  SETTINGS_VTAB_AGENTS: MODULE_SETTINGS_PATHS.BIOMETRIC_DEVICES.VTAB_AGENTS,
  SETTINGS_VTAB_DIRECT_TERMINALS:
    MODULE_SETTINGS_PATHS.BIOMETRIC_DEVICES.VTAB_DIRECT_TERMINALS,
});
